import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@services/apiClient';

/**
 * Public short payer link:
 * - User opens /p/:code
 * - We resolve to (dealId, token) via backend and redirect to /pay/:dealId?token=...
 */
export function PayShortRedirectPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const c = (code ?? '').trim();
    const invalidMsg = t('payer.errors.invalidPayerLink', { defaultValue: 'Invalid payer link.' });
    if (!c) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: set error state for invalid link code
      setError(invalidMsg);
      return;
    }

    apiClient
      .get<{ dealId: string; token: string } | { success?: boolean; data?: { dealId: string; token: string } }>(`/api/deals/p/${encodeURIComponent(c)}`)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data as unknown;
        const data =
          raw && typeof raw === 'object' && 'data' in (raw as object)
            ? ((raw as { data?: { dealId: string; token: string } }).data ?? null)
            : (raw as { dealId: string; token: string });
        if (!data?.dealId || !data?.token) throw new Error(invalidMsg);
        navigate(`/pay/${data.dealId}?token=${encodeURIComponent(data.token)}`, { replace: true });
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as { message?: string })?.message ?? invalidMsg);
      });

    return () => {
      cancelled = true;
    };
  }, [code, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 font-sans bg-surface-web">
        <p className="text-surface-error-primary text-center max-w-md" role="alert">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 font-sans bg-surface-web">
      <p className="text-primary" aria-busy="true">
        Loading payment link…
      </p>
    </div>
  );
}

